import { createComparator, getAbsoluteUrl } from '@tmapy/utils';
import { print } from 'graphql';
import { isIncompatibilityModules } from '../utils/isIncompatibilityModules.js';
import { transformLayerConfigFromTinker } from './transformLayerConfigFromTinker.js';
import { buildLayerSwitcherConfig } from './buildLayerSwitcherConfig.js';
import { buildMapSwitcherConfig } from './buildMapSwitcherConfig.js';
import { buildRoutesConfig } from './buildRoutesConfig.js';
import { buildFeatureRouteMappings } from './buildFeatureRouteMappings.js';
import { buildInfoConfig } from './buildInfoConfig.js';
import { buildMapSearchConfig } from './buildMapSearchConfig.js';
import { buildUnmanagedLayersConfig } from './buildUnmanagedLayersConfig.js';
import configFromTinker from './configFromTinker.graphql';
export const COMPATIBLE_VERSION = {
    tinker: { min: '6.0.0', max: '7.0.0' },
};
export const loadConfigFromTinker = async (appBaseName, tinkerGraphql = '/beg/tinker/graphql') => {
    const tinkerGraphQLUrl = getAbsoluteUrl(tinkerGraphql) ?? tinkerGraphql;
    try {
        const response = await fetch(tinkerGraphQLUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                query: print(configFromTinker),
                variables: {
                    slugId: appBaseName,
                },
            }),
        });
        if (!response.ok) {
            throw new Error(`Invalid response ${response.status} ${response.statusText}.`);
        }
        const responseJSON = await response.json();
        if (!responseJSON.data) {
            console.info(`%cIncompatibility between tinker and client!`, 'color:red');
            return { appId: appBaseName, incompatibility: true, hasAuthentication: false };
        }
        const tinkerConfig = responseJSON.data;
        const appConfig = tinkerConfig.apps.edges[0]?.node;
        const mapConfig = tinkerConfig.maps.edges[0]?.node;
        const incompatibility = isIncompatibilityModules(tinkerConfig.installedModules.edges, COMPATIBLE_VERSION, 'Installed basic components');
        if (!appConfig) {
            throw new Error(`Application is not set in the TWC.`);
        }
        if (tinkerConfig.apps.edges.length > 1) {
            throw new Error(`Multiple configurations found for one application.`);
        }
        if (tinkerConfig.maps.edges.length > 1) {
            throw new Error(`Multiple configurations found for one map.`);
        }
        const slugId = appConfig.slugIds.find((slugId) => slugId === appBaseName) ??
            appConfig.slugIds[0] ??
            appBaseName;
        let view;
        if (mapConfig) {
            const { maxZoom, minZoom, zoom, center, projection } = mapConfig;
            view = {
                maxZoom,
                minZoom,
                zoom,
                center,
                projection: projection.replace('_', ':'),
            };
            view.zoom = view.zoom ?? view.minZoom;
        }
        const layers = tinkerConfig.mapLayers.edges
            .sort(createComparator((asc) => asc.node.zIndex, (asc) => asc.node.mapService.url, (asc) => asc.node.order, (asc) => asc.node.id))
            .map(transformLayerConfigFromTinker)
            .filter(Boolean);
        const nonTranslatableResourceStrings = {};
        const info = buildInfoConfig(tinkerConfig, nonTranslatableResourceStrings);
        if (appConfig.title) {
            nonTranslatableResourceStrings['app.title'] = appConfig.title;
        }
        const layerSwitcher = buildLayerSwitcherConfig(tinkerConfig, nonTranslatableResourceStrings);
        const mapSwitcher = buildMapSwitcherConfig(tinkerConfig, nonTranslatableResourceStrings);
        const search = buildMapSearchConfig(tinkerConfig, nonTranslatableResourceStrings);
        const layerRoutes = buildRoutesConfig(tinkerConfig, nonTranslatableResourceStrings);
        const featureRouteMappings = buildFeatureRouteMappings(tinkerConfig);
        buildUnmanagedLayersConfig(tinkerConfig, nonTranslatableResourceStrings);
        return {
            appId: appConfig.nameId,
            basename: `/${slugId}`,
            origin: appConfig.origin,
            loadBeaConfig: appConfig.loadBeaConfig,
            hasAuthentication: appConfig.requireAuth,
            incompatibility,
            routes: layerRoutes,
            featureRouteMappings,
            map: {
                view,
                layers,
                zoomLevelShortcuts: mapConfig?.enableZoomLevelShortcuts ? [] : undefined,
            },
            info,
            measure: mapConfig?.enableMeasure && {},
            exportMap: mapConfig?.enableExportMap && {},
            userLocation: mapConfig?.enableUserLocation && {},
            notes: mapConfig?.enableMapNotes && {},
            search,
            layerSwitcher,
            mapSwitcher,
            activeWidget: mapConfig?.activeWidget,
            nonTranslatableResourceStrings,
            otherLinks: tinkerConfig.links.edges.map((link) => ({
                label: link.node.title,
                url: link.node.url,
            })),
            homeLink: appConfig.homeLink?.url,
        };
    }
    catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.info(`Cannot load valid tinker config from ${tinkerGraphQLUrl}. %c${message}`, 'color:red');
    }
    return {};
};
//# sourceMappingURL=loadConfigFromTinker.js.map